import React from 'react';
import './DeliveryIndicators.css';

// The four live delivery statuses. One list, imported by every dropdown and
// filter in this module, so a status can never be offered in one screen and
// missing from another. Mirrors DELIVERY_STATUSES in the backend model.
export const STATUS_OPTIONS = ['Pending', 'In Transit', 'Delivered', 'Failed'];

// Mirrors the `hasProof` virtual on the Delivery model. The API already sends
// it, but recompute as a fallback so a stale/plain response never renders the
// chip wrong — same pattern as getPaymentStatus in DeliveryManagement.js.
export const hasProof = (delivery) => {
    if (typeof delivery?.hasProof === 'boolean') return delivery.hasProof;
    return (delivery?.proofDocuments?.length > 0) ||
           Boolean(delivery?.proofPhoto) ||
           delivery?.proofStorage === 'sharepoint';
};

const statusClass = (status) => {
    switch (status) {
        case 'Delivered': return 'di-status-delivered';
        case 'In Transit': return 'di-status-transit';
        case 'Failed': return 'di-status-failed';
        case 'Pending': return 'di-status-pending';
        // TRANSITIONAL: an un-backfilled legacy row still has to render as
        // something. It reads as In Transit, which is what the backfill makes it.
        default: return 'di-status-transit';
    }
};

/**
 * The ONE rendering of a delivery's state: where it is, plus the two
 * independent facts about it.
 *
 *   Delivered · 📷 Proof · 🧾 Invoice pending
 *
 * Used by both the driver's card and the admin's row so the two screens
 * cannot drift apart — which is exactly what happened while proof and invoice
 * were statuses and each screen listed them by hand.
 *
 * Each chip renders only when its fact is true, so a branch that never uses
 * invoice-pending (Ahlamart, a supermarket) simply never sees that chip. The
 * field being optional is the whole mechanism — there is nothing to configure.
 */
const DeliveryIndicators = ({ delivery, showWarning = true }) => {
    const proof = hasProof(delivery);
    const invoicePending = Boolean(delivery?.invoicePending);
    // Proof is optional, so a driver can close a delivery without it. Optional
    // is not invisible: the office needs to see which ones to chase.
    const missingProof = showWarning && delivery?.status === 'Delivered' && !proof;

    return (
        <span className="di-row">
            <span className={`di-chip di-status ${statusClass(delivery?.status)}`}>
                {delivery?.status}
            </span>
            {proof && (
                <span className="di-chip di-proof" title="Proof attached">📷 Proof</span>
            )}
            {invoicePending && (
                <span className="di-chip di-invoice" title="Invoice not raised yet">🧾 Invoice pending</span>
            )}
            {missingProof && (
                <span className="di-chip di-warning" title="Delivered with no proof attached">⚠ No proof</span>
            )}
        </span>
    );
};

export default DeliveryIndicators;
